import React, { useEffect, useRef, useState } from "react";
import CustomBreadCrub from "./CustomBreadCrub";
import { IResponsiveGrid, ProductSortType } from "src/types/GlobalInterfaces";
import NOBanner from "../NOBanner";
import ErrorIcon from "src/assets/images/not-data/incident.png";
import GradientBtn from "../WebsiteComponents/GradientBtn";
import NoDataIcon from "src/assets/images/not-data/no-products-found.png";
import { ImageBaseUrl, ResponsiveGrid } from "src/lib/constans";
import { CircularProgress } from "@mui/material";

interface IProps {
  breadCrumbData?: {
    label: string;
    onClick?: () => void;
    highlight?: boolean;
    arrow?: boolean;
  }[];
  heading?: string;
  renderProduct?: (data: any, index: number) => React.ReactNode;
  loading?: boolean;
  banner?: boolean;
  bannerLoading?: boolean;
  bannerData?: {
    title?: string;
    logo?: string;
    description?: string;
    image?: string;
  };
  products?: any[];
  totalProducts?: number;
  totalPages?: number;
  currentPage?: number;
  dataLimit?: number;
  showSort?: boolean;
  onSortChange?: (sort: ProductSortType) => void;
  sortOptions?: {
    label: string;
    value: ProductSortType;
  }[];
  currentSort?: string;
  responsiveGrid?: IResponsiveGrid;
  className?: string;
  renderSkeleton?: () => React.ReactNode;
  onLoadMore?: (page: number) => void;
  error?: string;
  onLoadAgain?: (page: number) => void;
}

const ProductsViewContainer = ({
  breadCrumbData,
  heading,
  renderProduct,
  loading = false,
  banner = true,
  products = [],
  totalProducts = 0,
  totalPages = 1,
  currentPage = 1,
  dataLimit = 10,
  bannerData,
  bannerLoading = false,
  showSort = true,
  onSortChange,
  sortOptions = [],
  currentSort,
  responsiveGrid = ResponsiveGrid,
  className,
  renderSkeleton = () => (
    <div className="w-full h-fit bg-gray-200 rounded-lg animate-pulse"></div>
  ),
  onLoadMore,
  error,
  onLoadAgain,
}: IProps) => {
  const eleRef = useRef<HTMLDivElement | null>(null);
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);
  const [isDescriptionOverflow, setIsDescriptionOverflow] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // isDescriptionOverflow: tracks if description exceeds 3 lines
  // isDescriptionExpanded: tracks if user clicked "See More" to expand full description

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          if (
            currentPage &&
            currentPage < (totalPages || 1) &&
            onLoadMore &&
            !loading
          ) {
            setTimeout(() => {
              onLoadMore?.((currentPage || 1) + 1);
            }, 100);
          }
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    if (eleRef.current) {
      observer.observe(eleRef.current);
    }

    return () => {
      if (eleRef.current) {
        observer.unobserve(eleRef.current);
      }
    };
  }, [eleRef, currentPage, totalPages, loading]);

  useEffect(() => {
    if (descriptionRef.current && bannerData?.description) {
      const element = descriptionRef.current;
      const lineHeight = parseInt(window.getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 4;
      const actualHeight = element.scrollHeight;

      setIsDescriptionOverflow(actualHeight > maxHeight);
    }
  }, [bannerData?.description]);

  return (
    <div className={`product-view-container-width p-[2px] ${className}`}>
      <div className="w-full box-design min-h-full p-2 2xl:p-3">
        {breadCrumbData && <CustomBreadCrub data={breadCrumbData} />}
        {bannerLoading ? (
          <div className="w-full h-[180px] xl:h-[230px] shadow-all bg-image-loader rounded-lg overflow-hidden"></div>
        ) : (
          banner && (
            <div className="w-full h-fit py-2 2xl:py-3">
              <div
                className={`w-full h-[180px] lg:h-[25vh] shadow-all-light ${
                  bannerData?.image ? "" : "bg-image-loader"
                } rounded-md 2xl:rounded-lg overflow-hidden`}
              >
                {bannerData?.image ? (
                  <img
                    src={bannerData?.image}
                    onError={(e) => {
                      // e.currentTarget.src = "/assets/images/banner.png";
                    }}
                    alt="banner"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <NOBanner title={bannerData?.title || heading || ""} />
                )}
              </div>
              {bannerData?.logo && (
                <div className="w-full pl-[30px] mt-[-50px] lg:mt-[-3vw] 2xl:mt-[-2.5vw] relative z-10">
                  <div className="w-[80px] lg:w-[6vw] 2xl:w-[5vw] aspect-square p-1.5 2xl:p-2 bg-white rounded-lg overflow-hidden shadow-all">
                    <img
                      src={bannerData?.logo}
                      alt="logo"
                      className="w-full h-full object-contain rounded-sm 2xl:rounded-[0.7vw]"
                    />
                  </div>
                </div>
              )}
              {bannerData?.title && (
                <div className="w-full mt-2 relative">
                  <h1 className="font-Poppins-SemiBold lowercase first-letter:uppercase font-size-18px text-[#2D2D2D]">
                    {bannerData?.title}
                  </h1>
                  <p
                    ref={descriptionRef}
                    className={`font-Poppins-Regular font-size-15px text-[#2D2D2D] mt-1 ${
                      !isDescriptionExpanded ? "line-clamp-3" : ""
                    }`}
                  >
                    {bannerData?.description}
                  </p>
                  {isDescriptionOverflow && (
                    <button
                      onClick={() =>
                        setIsDescriptionExpanded(!isDescriptionExpanded)
                      }
                      className={`text-[#00443F] w-full h-[50%] font-Poppins-Medium font-size-14px mt-2 hover:underline cursor-pointer flex items-end justify-center ${isDescriptionExpanded ? "" : "absolute bottom-0 bg-gradient-to-b from-transparent to-white"}`}
                    >
                      {isDescriptionExpanded ? "See Less" : "See More"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        )}
        {showSort && (
          <div className="w-full border-t border-b border-[#E6E6E6] h-fit">
            <div className="w-full h-[35px] 2xl:h-[50px] flex items-center gap-4">
              <h2 className="font-Poppins-Medium font-size-16px text-[#2D2D2D]">
                Sort by
              </h2>
              <div className="flex-1 h-full relative">
                <div className="w-fit h-full absolute top-0 left-0 min-w-full flex items-center gap-4">
                  {sortOptions?.map((option) => (
                    <div
                      key={option.value}
                      className={`font-Poppins-Regular h-full px-2 font-size-16px cursor-pointer flex items-center justify-center text-[#404040] border-b-2 ${
                        currentSort === option.value
                          ? "border-[#00443F]"
                          : "border-transparent"
                      }`}
                      onClick={() => onSortChange?.(option.value)}
                      id={`sort-by-${option.value}`}
                    >
                      <span className="mb-[-2px]">{option.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <div
          className={`w-full h-fit grid md:gap-1.5 lg:gap-2 2xl:gap-3 grid-cols-1 md:grid-cols-3 lg:grid-cols-4 mt-3`}
          // className={`w-full h-fit grid gap-${responsiveGrid.xs?.gap} xs:gap-${responsiveGrid.sm?.gap} sm:gap-${responsiveGrid.sm?.gap} md:gap-${responsiveGrid.md?.gap} lg:gap-${responsiveGrid.lg?.gap} xl:gap-${responsiveGrid.xl?.gap} 2xl:gap-${responsiveGrid["2xl"]?.gap} grid-cols-1 xs:grid-cols-1 sm:grid-cols-${responsiveGrid.sm?.cols} md:grid-cols-${responsiveGrid.md?.cols} lg:grid-cols-${responsiveGrid.lg?.cols} xl:grid-cols-${responsiveGrid.xl?.cols} 2xl:grid-cols-${responsiveGrid["2xl"]?.cols} mt-3`}
        >
          {products.length > 0 &&
            products.map((product, index) => (
              <div key={product?.id} className="w-full h-fit">
                {renderProduct?.(product, index)}
              </div>
            ))}
          {loading &&
            products.length === 0 &&
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="w-full h-fit">
                {renderSkeleton()}
              </div>
            ))}
        </div>
        {loading && products.length > 0 && (
          <div className="w-full h-[10vh] flex items-center justify-center">
            <CircularProgress size={20} color="primary" />{" "}
            <span className="font-Poppins-Regular font-size-16px text-[#2D2D2D] ml-2">
              Loading...
            </span>
          </div>
        )}
        {products.length === 0 && !loading && (
          <div className="w-full min-h-[250px] flex flex-col items-center justify-center gap-1">
            <img
              src={NoDataIcon}
              alt="No products found"
              className="w-[clamp(80px,5.5vw,150px)] h-[clamp(80px,5.5vw,150px)] object-contain"
            />
            <p className="font-Poppins-Regular font-size-16px text-[#2D2D2D]">
              No products found
            </p>
          </div>
        )}
        {error && (
          <div className="w-full min-h-[120px] flex flex-col items-center justify-center gap-1">
            <img
              src={ErrorIcon}
              alt="Error While Fetching..."
              className="w-[clamp(80px,5.5vw,150px)] h-[clamp(80px,5.5vw,150px)] object-contain"
            />
            <p className="font-Poppins-Regular font-size-16px text-[#2D2D2D]">
              {error || "An error occurred while fetching products."}
            </p>
            <GradientBtn
              ButtonText="Try Again"
              onPress={() => {
                onLoadAgain?.(currentPage + 1);
              }}
              btnExtraStyle={{
                width: "fit-content",
              }}
              fontfamilyBtn="font-Poppins-Regular"
              fontsizeBtn="font-size-16px"
            />
          </div>
        )}
        {currentPage && currentPage < (totalPages || 1) && (
          <div
            ref={eleRef}
            className="w-full h-10 absolute bottom-0 left-0"
          ></div>
        )}
      </div>
    </div>
  );
};

export default ProductsViewContainer;
